import AsyncStorage from "@react-native-async-storage/async-storage";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  doc,
  updateDoc,
  deleteField,
  serverTimestamp,
  waitForPendingWrites,
} from "firebase/firestore";
import { firebaseApp } from "../firebase/config";

const DEFERRED_MEDIA_QUEUE_KEY = "deferredMediaUploadQueueV1";

const parseQueue = (rawValue) => {
  if (!rawValue) {
    return [];
  }

  try {
    const parsed = JSON.parse(rawValue);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const getQueue = async () => {
  const rawValue = await AsyncStorage.getItem(DEFERRED_MEDIA_QUEUE_KEY);
  return parseQueue(rawValue);
};

const saveQueue = async (queue) => {
  await AsyncStorage.setItem(DEFERRED_MEDIA_QUEUE_KEY, JSON.stringify(queue));
};

const getDocRefFromPath = (db, path) => {
  const segments = String(path || "")
    .split("/")
    .map((segment) => segment.trim())
    .filter(Boolean);

  if (segments.length < 2 || segments.length % 2 !== 0) {
    throw new Error(`Invalid Firestore document path: ${path}`);
  }

  return doc(db, ...segments);
};

const uploadDeferredMedia = async (item) => {
  const mediaUris = Array.isArray(item.mediaUris) ? item.mediaUris : [];
  if (!mediaUris.length) {
    return [];
  }

  const storage = getStorage(firebaseApp);
  const uploadTimestamp = Date.now();

  const uploadedUrls = await Promise.all(
    mediaUris.map(async (uri, index) => {
      const response = await fetch(uri);
      const blob = await response.blob();
      const extension =
        typeof blob.type === "string" && blob.type.includes("/")
          ? blob.type.split("/")[1]
          : "jpg";

      const objectPath = `${item.storageBasePath}/${uploadTimestamp}_${index}.${extension}`;
      const storageRef = ref(storage, objectPath);
      const snapshot = await uploadBytes(storageRef, blob);
      return getDownloadURL(snapshot.ref);
    })
  );

  return uploadedUrls;
};

export const queueDeferredMediaUpload = async ({
  label,
  mediaUris,
  storageBasePath,
  documentPaths,
  documentTargets,
}) => {
  const queue = await getQueue();
  const queueId = `media_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  queue.push({
    id: queueId,
    label: label || "media-upload",
    createdAt: Date.now(),
    mediaUris: Array.isArray(mediaUris) ? mediaUris : [],
    storageBasePath,
    documentPaths: Array.isArray(documentPaths) ? documentPaths : [],
    documentTargets: Array.isArray(documentTargets) ? documentTargets : [],
  });

  await saveQueue(queue);
  return queueId;
};

export const getDeferredMediaQueueCount = async () => {
  const queue = await getQueue();
  return queue.length;
};

export const flushDeferredMediaUploads = async (db) => {
  const queue = await getQueue();
  if (!queue.length) {
    return { flushed: 0, failed: 0, remaining: 0 };
  }

  try {
    // Ensure dependent offline Firestore writes are committed before patching image URLs.
    await waitForPendingWrites(db);
  } catch {
    // Continue: this can fail when there are no pending writes.
  }

  const remainingItems = [];
  let flushedCount = 0;
  let failedCount = 0;

  for (const item of queue) {
    try {
      const urls = await uploadDeferredMedia(item);
      const explicitTargets = Array.isArray(item.documentTargets)
        ? item.documentTargets
        : [];
      const fallbackTargets = Array.isArray(item.documentPaths)
        ? item.documentPaths.map((path) => ({ path, patchType: "images-array" }))
        : [];
      const resolvedTargets = explicitTargets.length ? explicitTargets : fallbackTargets;

      for (const target of resolvedTargets) {
        const targetRef = getDocRefFromPath(db, target.path);
        const patchType = target.patchType || "images-array";
        const mediaPatch =
          patchType === "first-image-url"
            ? { image_url: urls[0] || "" }
            : { images: urls };

        await updateDoc(targetRef, {
          ...mediaPatch,
          mediaUploadStatus: "uploaded",
          mediaUploadedAt: serverTimestamp(),
          pendingMediaCount: deleteField(),
          queuedMediaUploadId: deleteField(),
        });
      }

      flushedCount += 1;
    } catch (error) {
      failedCount += 1;
      remainingItems.push(item);
      console.log("Deferred media upload failed:", item?.id, error?.message || error);
    }
  }

  if (remainingItems.length) {
    await saveQueue(remainingItems);
  } else {
    await AsyncStorage.removeItem(DEFERRED_MEDIA_QUEUE_KEY);
  }

  return {
    flushed: flushedCount,
    failed: failedCount,
    remaining: remainingItems.length,
  };
};
